import React from "react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { SavedIdeas as SavedIdeasComponent } from "@/components/SavedIdeas";
import { Loader2, Heart, Lightbulb, Hammer, Rocket, ArrowRight } from "lucide-react";
import { useFavorites } from "@/hooks/useFavorites";

const SavedIdeasPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { favorites, savedIdeas, inProgressIdeas, shippedIdeas, isLoading } = useFavorites();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/signin?next=/saved");
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalIdeas = favorites?.length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="space-y-8">
          {/* Header with workflow explanation */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <Heart className="h-6 w-6 text-primary fill-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Saved Ideas</h1>
                <p className="text-muted-foreground">
                  Your journey from discovery to shipped product
                </p>
              </div>
            </div>

            {/* Workflow explanation */}
            <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 rounded-xl border border-border/50 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                <span>Save ideas</span>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50" />
              <div className="flex items-center gap-2 text-muted-foreground">
                <Hammer className="h-4 w-4 text-primary" />
                <span>Build projects</span>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50" />
              <div className="flex items-center gap-2 text-muted-foreground">
                <Rocket className="h-4 w-4 text-green-500" />
                <span>Ship apps</span>
              </div>
            </div>

            {/* Progress Stats */}
            {!isLoading && totalIdeas > 0 && (
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-muted/30 border border-border/50 text-center">
                  <div className="text-3xl font-bold text-muted-foreground">
                    {savedIdeas.length}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">Saved</div>
                  <div className="text-xs text-muted-foreground/60 mt-0.5">Ready to build</div>
                </div>
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 text-center">
                  <div className="text-3xl font-bold text-primary">
                    {inProgressIdeas.length}
                  </div>
                  <div className="text-sm text-primary mt-1">In Progress</div>
                  <div className="text-xs text-muted-foreground/60 mt-0.5">Currently building</div>
                </div>
                <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/20 text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {shippedIdeas.length}
                  </div>
                  <div className="text-sm text-green-600 mt-1">Shipped</div>
                  <div className="text-xs text-muted-foreground/60 mt-0.5">Live & running</div>
                </div>
              </div>
            )}
          </div>

          {/* Saved Ideas Component */}
          <SavedIdeasComponent />
        </div>
      </div>
    </div>
  );
};

export default SavedIdeasPage;